import React, { useState, useEffect, useRef } from 'react';
import './DotSplitGenerator.css';

const DotSplitGenerator = () => {
  const canvasRef = useRef(null);
  const [gridSize, setGridSize] = useState(6);
  const [stepsCount, setStepsCount] = useState(5);
  const [iterations, setIterations] = useState(3000);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ show: false, percent: 0, tested: 0, score: 0 });
  const [showingSolution, setShowingSolution] = useState(false);
  const [puzzle, setPuzzle] = useState(null);
  const [info, setInfo] = useState(null);

  const CONFIG = { CELL: 60, PAD: 20, WIDTH: 600 };
  const circleGlyph = ["","①","②","③","④","⑤","⑥","⑦","⑧","⑨"];

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = setupCanvas(canvasRef.current, CONFIG.WIDTH, 720);
      ctx.font = '20px Arial';
      ctx.fillStyle = '#777';
      ctx.textAlign = 'center';
      ctx.fillText('Click "Generate Best Puzzle" to start', CONFIG.WIDTH/2, 360);
    }
  }, []);

  function setupCanvas(canvas, cssWidth, cssHeight) {
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = cssWidth + 'px';
    canvas.style.height = cssHeight + 'px';
    canvas.width = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
  }

  const inBounds = (p, n) => p.x >= 0 && p.y >= 0 && p.x < n && p.y < n;
  const manhattan = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  const key = (p) => `${p.x},${p.y}`;

  function blockedSetForSegment(grid, a, b) {
    const s = new Set();
    const startKey = key(grid.start);
    const aKey = key(a);
    const bKey = key(b);
    if (startKey !== aKey && startKey !== bKey) {
      s.add(startKey);
    }
    for (const pt of grid.dots) {
      const ptKey = key(pt);
      if (ptKey !== aKey && ptKey !== bKey) {
        s.add(ptKey);
      }
    }
    return s;
  }

  function shortestPathLength(from, to, gridSize, blocked) {
    const dirs = [{x:1,y:0}, {x:-1,y:0}, {x:0,y:1}, {x:0,y:-1}];
    const queue = [from];
    const seen = new Set([key(from)]);
    let steps = 0;

    while (queue.length) {
      const levelSize = queue.length;
      for (let i = 0; i < levelSize; i++) {
        const cur = queue.shift();
        if (cur.x === to.x && cur.y === to.y) return steps;

        for (const dir of dirs) {
          const nx = cur.x + dir.x;
          const ny = cur.y + dir.y;
          if (nx < 0 || ny < 0 || nx >= gridSize || ny >= gridSize) continue;

          const kk = `${nx},${ny}`;
          const isGoal = (nx === to.x && ny === to.y);
          if (!isGoal && blocked.has(kk)) continue;
          if (seen.has(kk)) continue;

          seen.add(kk);
          queue.push({x: nx, y: ny});
        }
      }
      steps++;
    }
    return Infinity;
  }

  function pathDistance(grid, a, b) {
    return shortestPathLength(a, b, grid.size, blockedSetForSegment(grid, a, b));
  }

  function calculateActualDistance(fromPos, toPos, gridSize, occupiedCells) {
    const tempOccupied = new Set(occupiedCells);
    tempOccupied.delete(key(fromPos));
    tempOccupied.delete(key(toPos));

    const path = findPathAvoidingDots(fromPos, toPos, gridSize, tempOccupied);
    return path.length - 1;
  }

  function recalculateStepCountsWithDetours(grid) {
    const pts = [grid.start, ...grid.dots];
    const allOccupied = new Set();
    allOccupied.add(key(grid.start));
    for (const dot of grid.dots) {
      allOccupied.add(key(dot));
    }

    const actualStepCounts = [];
    let hasDetours = false;
    for (let i = 0; i < grid.stepCounts.length; i++) {
      const from = pts[i];
      const to = grid.dots[i];
      const originalDist = grid.stepCounts[i];
      const actualDist = calculateActualDistance(from, to, grid.size, allOccupied);
      actualStepCounts.push(actualDist);
      if (actualDist !== originalDist) {
        hasDetours = true;
        console.log(`Step ${i+1}: adjusted distance from ${originalDist} to ${actualDist} (detour required)`);
      }
    }

    if (hasDetours) {
      console.log('Puzzle adjusted for detours:', actualStepCounts);
    }

    return actualStepCounts;
  }

  class DotGrid {
    constructor(size, start, dots, stepCounts) {
      this.size = size;
      this.start = {...start};
      this.dots = dots.map(p => ({...p}));
      this.stepCounts = [...stepCounts];
    }
  }

  function buildDistanceIndex(grid) {
    const idx = { fromStart: new Map(), between: new Map() };
    const M = grid.dots.length;

    for (let i = 0; i < M; i++) {
      const d = pathDistance(grid, grid.start, grid.dots[i]);
      if (d === Infinity) continue;
      const arr = idx.fromStart.get(d) || [];
      arr.push(i);
      idx.fromStart.set(d, arr);
    }

    for (let i = 0; i < M; i++) {
      for (let j = 0; j < M; j++) {
        if (i === j) continue;
        const d = pathDistance(grid, grid.dots[i], grid.dots[j]);
        if (d === Infinity) continue;
        const m = idx.between.get(i) || new Map();
        const arr = (m.get(d) || []);
        arr.push(j);
        m.set(d, arr);
        idx.between.set(i, m);
      }
    }
    return idx;
  }

  function validateSolutionPath(grid, orderSeq) {
    const pts = [grid.start, ...orderSeq.map(i => grid.dots[i])];

    const allDots = new Set();
    allDots.add(key(grid.start));
    for (const dot of grid.dots) {
      allDots.add(key(dot));
    }

    for (let i = 0; i < pts.length - 1; i++) {
      const from = pts[i];
      const to = pts[i + 1];

      const occupied = new Set(allDots);
      occupied.delete(key(from));
      occupied.delete(key(to));

      const path = findPathAvoidingDots(from, to, grid.size, occupied);

      if (path.length === 2 && manhattan(from, to) > 1) {
        if (occupied.size > 0) {
          return false;
        }
      }
    }

    return true;
  }

  function analyzeOrders(grid) {
    const idx = buildDistanceIndex(grid);
    const K = grid.stepCounts.length;
    const widths = [];
    const expansions = [];
    let frontier = [{ last: 'S', used: new Set(), seq: [] }];
    for (let t = 0; t < K; t++) {
      const step = grid.stepCounts[t];
      const next = [];
      let stepExp = 0;
      for (const st of frontier) {
        let candidates = [];
        if (st.last === 'S') {
          candidates = (idx.fromStart.get(step) || []);
        } else {
          const map = idx.between.get(st.last) || new Map();
          candidates = (map.get(step) || []);
        }
        for (const to of candidates) {
          if (st.used.has(to)) continue;
          const used2 = new Set(st.used);
          used2.add(to);
          next.push({ last: to, used: used2, seq: [...st.seq, to] });
          stepExp++;
        }
      }
      expansions.push(stepExp);
      widths.push(next.length);
      frontier = next;
      if (frontier.length === 0) break;
    }
    const orders = frontier.map(st => st.seq);
    const unique = orders.length === 1;
    let collapseStep = widths.length;
    for (let i = 0; i < widths.length; i++) {
      if (widths[i] === 1) {
        collapseStep = i + 1;
        break;
      }
    }
    const totalNodes = expansions.reduce((a, b) => a + b, 0);
    const widthSum = widths.reduce((a, b) => a + b, 0);
    const maxWidth = widths.length ? Math.max(...widths) : 0;
    const ambiguousSteps = widths.filter(c => c > 1).length;

    let detours = 0;
    if (unique && orders.length > 0) {
      detours = countDetours(grid, orders[0]);
    }

    return { unique, orders, widths, expansions, collapseStep, totalNodes, widthSum, maxWidth, ambiguousSteps, detours };
  }

  function countDetours(grid, orderSeq) {
    const pts = [grid.start, ...orderSeq.map(i => grid.dots[i])];

    const occupied = new Set();
    occupied.add(key(grid.start));
    for (const dot of grid.dots) {
      occupied.add(key(dot));
    }

    let detourCount = 0;

    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      const directDist = manhattan(a, b);

      const tempOccupied = new Set(occupied);
      tempOccupied.delete(key(a));
      tempOccupied.delete(key(b));

      const path = findPathAvoidingDots(a, b, grid.size, tempOccupied);
      const actualDist = path.length - 1;

      if (actualDist > directDist) {
        detourCount++;
      }
    }

    return detourCount;
  }

  function branchingScore(a, K) {
    if (!a.unique) return -Infinity;
    let s = 0;
    s += a.widthSum * 22;
    s += a.maxWidth * 70;
    s += a.ambiguousSteps * 45;
    s += a.collapseStep * 35;
    s += Math.log2(a.totalNodes + 1) * 25;
    s += K * 6;

    if (a.collapseStep > K - 2) {
      s -= 200;
    }

    const middleStart = Math.floor(K / 3);
    const middleEnd = Math.floor(2 * K / 3);
    if (a.collapseStep >= middleStart && a.collapseStep <= middleEnd) {
      s += 150;
    }

    const narrowPoints = a.widths.filter(w => w <= 2).length;
    s += narrowPoints * 40;

    return Math.round(s);
  }

  function branchingScoreWithGrid(a, K, grid) {
    let s = branchingScore(a, K);

    const maxDist = Math.max(...grid.stepCounts);
    const collapseIndex = a.collapseStep - 1;

    if (collapseIndex >= 0 && collapseIndex < grid.stepCounts.length) {
      const collapseDistance = grid.stepCounts[collapseIndex];
      if (collapseDistance === maxDist && maxDist > 3) {
        s -= 180;
      }
    }

    const uniqueDistances = new Set(grid.stepCounts).size;
    s += uniqueDistances * 15;

    if (grid.stepCounts[K - 1] === maxDist) {
      s -= 100;
    }

    if (a.detours > 0) {
      s += a.detours * 120;
    }

    return Math.round(s);
  }

  function createPuzzlePlan(K, B) {
    const plan = [];
    let remaining = K;

    const initialBranches = 1 + (Math.random() < 0.5 ? 1 : 0);
    for (let i = 0; i < initialBranches && remaining > 0; i++) {
      plan.push('B');
      remaining--;
    }

    while (remaining > 2) {
      const r = Math.random();
      if (r < 0.3 && remaining > 3) {
        plan.push('B');
      } else if (r < 0.6 && remaining > 2) {
        plan.push('C');
      } else {
        plan.push('U');
      }
      remaining--;
    }

    while (remaining > 0) {
      plan.push('S');
      remaining--;
    }

    return plan;
  }

  function choosePartialConstrain(centers, usedSet, n) {
    const maxD = 2 * (n - 1);
    const D = Array.from({length: maxD}, (_, i) => i + 1);
    for (let i = D.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [D[i], D[j]] = [D[j], D[i]];
    }

    for (const dist of D) {
      const candSets = centers.map(c => ringCells(c, dist, n).filter(p => !usedSet.has(key(p))));
      const nonEmpty = candSets.filter(s => s.length > 0);

      if (nonEmpty.length >= 2 && nonEmpty.length < centers.length) {
        const picks = [];
        for (const set of candSets) {
          if (set.length > 0) {
            const arr = [...set];
            const choice = arr[(Math.random() * arr.length) | 0];
            picks.push(choice);
          }
        }
        if (picks.length >= 2) {
          return { dist, picks };
        }
      }

      if (centers.length === 2 && nonEmpty.length === 2) {
        const picks = pickDistinct(candSets.map(arr => new Set(arr.map(p => ({...p})))));
        if (picks) return { dist, picks };
      }
    }

    return null;
  }

  function ringCells(center, dist, n) {
    const cells = [];
    if (dist <= 0) return cells;
    for (let dx = -dist; dx <= dist; dx++) {
      const dy = dist - Math.abs(dx);
      const y1 = center.y + dy;
      const y2 = center.y - dy;
      const x1 = center.x + dx;
      const p1 = {x: x1, y: y1}, p2 = {x: x1, y: y2};
      if (inBounds(p1, n)) cells.push(p1);
      if (dy !== 0 && inBounds(p2, n)) cells.push(p2);
    }
    const seen = new Set();
    const out = [];
    for (const c of cells) {
      const k = key(c);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
    return out;
  }

  function pickDistinct(candSets) {
    const chosen = [];
    const used = new Set();
    for (const set of candSets) {
      const arr = [...set];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      let pick = null;
      for (const c of arr) {
        const k = key(c);
        if (!used.has(k)) {
          pick = c;
          break;
        }
      }
      if (!pick) return null;
      chosen.push(pick);
      used.add(key(pick));
    }
    return chosen;
  }

  function chooseSForBranches(centers, usedSet, n) {
    const maxD = 2 * (n - 1);
    const D = Array.from({length: maxD}, (_, i) => i + 1);
    for (let i = D.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [D[i], D[j]] = [D[j], D[i]];
    }
    for (const dist of D) {
      const candSets = centers.map(c => ringCells(c, dist, n).filter(p => !usedSet.has(key(p))));
      if (candSets.every(s => s.length > 0)) {
        const picks = pickDistinct(candSets.map(arr => new Set(arr.map(p => ({...p})))));
        if (picks) return { dist, picks };
      }
    }
    return null;
  }

  function chooseSUnique(centers, usedSet, n) {
    const maxD = 2 * (n - 1);
    const D = Array.from({length: maxD}, (_, i) => i + 1);
    for (let i = D.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [D[i], D[j]] = [D[j], D[i]];
    }
    for (const dist of D) {
      const lists = centers.map(c => ringCells(c, dist, n).filter(p => !usedSet.has(key(p))));
      const counts = lists.map(l => l.length);
      const nonEmpty = counts.map((c, i) => c > 0 ? i : -1).filter(i => i >= 0);
      if (nonEmpty.length === 1) {
        const b = nonEmpty[0];
        const arr = lists[b];
        const choice = arr[(Math.random() * arr.length) | 0];
        return { dist, branchIndex: b, pick: choice };
      }
    }
    return null;
  }

  function chooseSSingle(center, usedSet, n) {
    const maxD = 2 * (n - 1);
    const D = Array.from({length: maxD}, (_, i) => i + 1);
    for (let i = D.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [D[i], D[j]] = [D[j], D[i]];
    }
    for (const dist of D) {
      const arr = ringCells(center, dist, n).filter(p => !usedSet.has(key(p)));
      if (arr.length) {
        const choice = arr[(Math.random() * arr.length) | 0];
        return { dist, pick: choice };
      }
    }
    return null;
  }

  function topLeftNumbering(dots) {
    const idx = dots.map((p, i) => ({i, x: p.x, y: p.y}));
    idx.sort((a, b) => (a.y - b.y) || (a.x - b.x));
    const sorted = idx.map(e => ({x: e.x, y: e.y}));
    return { sorted };
  }

  function generateBestPuzzle(n, K, iterations, progressCb) {
    const B = n >= 7 ? 3 : 2;
    let best = null, bestScore = -Infinity, tested = 0;

    for (let it = 0; it < iterations; it++) {
      const start = { x: (Math.random() * n) | 0, y: (Math.random() * n) | 0 };

      const plan = createPuzzlePlan(K, B);

      const used = new Set([key(start)]);
      const dots = [];
      let stepCounts = [];
      let centers = [start];

      let failed = false;

      for (let step = 0; step < plan.length; step++) {
        const action = plan[step];

        if (action === 'B') {
          if (centers.length === 1) {
            centers = Array.from({length: B}, _ => ({...centers[0]}));
          }
          const choice = chooseSForBranches(centers, used, n);
          if (!choice) {
            failed = true;
            break;
          }
          stepCounts.push(choice.dist);
          for (const p of choice.picks) {
            dots.push({...p});
            used.add(key(p));
          }
          centers = choice.picks.map(p => ({...p}));
        }
        else if (action === 'C') {
          const partial = choosePartialConstrain(centers, used, n);
          if (!partial) {
            failed = true;
            break;
          }
          stepCounts.push(partial.dist);
          for (const p of partial.picks) {
            dots.push({...p});
            used.add(key(p));
          }
          centers = partial.picks.map(p => ({...p}));
        }
        else if (action === 'U') {
          const unq = chooseSUnique(centers, used, n);
          if (!unq) {
            failed = true;
            break;
          }
          stepCounts.push(unq.dist);
          dots.push({...unq.pick});
          used.add(key(unq.pick));
          centers = [{...unq.pick}];
        }
        else if (action === 'S') {
          const one = chooseSSingle(centers[0], used, n);
          if (!one) {
            failed = true;
            break;
          }
          stepCounts.push(one.dist);
          dots.push({...one.pick});
          used.add(key(one.pick));
          centers = [{...one.pick}];
        }
      }

      if (failed) {
        if (progressCb && it % 10 === 0) progressCb(it, iterations, tested, bestScore);
        continue;
      }

      const { sorted } = topLeftNumbering(dots);
      const grid = new DotGrid(n, start, sorted, stepCounts);

      const actualStepCounts = recalculateStepCountsWithDetours(grid);
      const gridWithActualCounts = new DotGrid(n, start, sorted, actualStepCounts);

      const analysis = analyzeOrders(gridWithActualCounts);
      if (!analysis.unique) {
        if (progressCb && it % 10 === 0) progressCb(it, iterations, tested, bestScore);
        continue;
      }

      const isValid = validateSolutionPath(gridWithActualCounts, analysis.orders[0]);
      if (!isValid) {
        if (progressCb && it % 10 === 0) progressCb(it, iterations, tested, bestScore);
        continue;
      }

      const score = branchingScoreWithGrid(analysis, K, gridWithActualCounts);
      tested++;
      if (score > bestScore) {
        bestScore = score;
        best = {grid: gridWithActualCounts, analysis, score};
      }
      if (progressCb && it % 10 === 0) progressCb(it, iterations, tested, bestScore);
    }
    return best;
  }

  function drawGridOnly(ctx, grid) {
    const cs = CONFIG.CELL, pad = CONFIG.PAD;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1.5;
    for (let y = 0; y < grid.size; y++) {
      for (let x = 0; x < grid.size; x++) {
        const px = pad + x * cs, py = pad + y * cs;
        ctx.strokeRect(px, py, cs, cs);
      }
    }
    ctx.fillStyle = '#000';
    ctx.font = 'bold 32px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', pad + grid.start.x * cs + cs / 2, pad + grid.start.y * cs + cs / 2);
    ctx.fillStyle = '#000';
    ctx.font = 'bold 28px Arial';
    for (let i = 0; i < grid.dots.length; i++) {
      const d = grid.dots[i];
      const label = i + 1 <= 9 ? circleGlyph[i + 1] : String(i + 1);
      ctx.fillText(label, pad + d.x * cs + cs / 2, pad + d.y * cs + cs / 2);
    }
  }

  function findPathAvoidingDots(fromGrid, toGrid, gridSize, occupiedCells) {
    const directions = [{x:1,y:0}, {x:-1,y:0}, {x:0,y:1}, {x:0,y:-1}];
    const queue = [{pos: fromGrid, path: [fromGrid]}];
    const visited = new Set([key(fromGrid)]);

    while (queue.length > 0) {
      const {pos, path} = queue.shift();

      if (pos.x === toGrid.x && pos.y === toGrid.y) {
        return path;
      }

      for (const dir of directions) {
        const next = {x: pos.x + dir.x, y: pos.y + dir.y};
        const k = key(next);

        if (next.x < 0 || next.y < 0 || next.x >= gridSize || next.y >= gridSize) continue;

        if (visited.has(k)) continue;

        if (occupiedCells.has(k) && !(next.x === toGrid.x && next.y === toGrid.y)) continue;

        visited.add(k);
        queue.push({pos: next, path: [...path, next]});
      }
    }

    return [fromGrid, toGrid];
  }

  function calculateSmartAnchor(gridPoint, prevGridPoint, nextGridPoint, cellSize, isDot) {
    if (!isDot) {
      return {x: 0, y: 0};
    }

    const offset = cellSize * 0.30;

    let fromDir = null;
    if (prevGridPoint) {
      const dx = gridPoint.x - prevGridPoint.x;
      const dy = gridPoint.y - prevGridPoint.y;
      if (dx > 0) fromDir = 'W';
      else if (dx < 0) fromDir = 'E';
      else if (dy > 0) fromDir = 'N';
      else if (dy < 0) fromDir = 'S';
    }

    let toDir = null;
    if (nextGridPoint) {
      const dx = nextGridPoint.x - gridPoint.x;
      const dy = nextGridPoint.y - gridPoint.y;
      if (dx > 0) toDir = 'E';
      else if (dx < 0) toDir = 'W';
      else if (dy > 0) toDir = 'S';
      else if (dy < 0) toDir = 'N';
    }

    let offsetX = 0, offsetY = 0;

    if (!prevGridPoint && nextGridPoint) {
      if (toDir === 'E') offsetX = offset;
      else if (toDir === 'W') offsetX = -offset;
      else if (toDir === 'S') offsetY = offset;
      else if (toDir === 'N') offsetY = -offset;
    }
    else if (prevGridPoint && !nextGridPoint) {
      if (fromDir === 'W') offsetX = -offset;
      else if (fromDir === 'E') offsetX = offset;
      else if (fromDir === 'N') offsetY = -offset;
      else if (fromDir === 'S') offsetY = offset;
    }
    else if (prevGridPoint && nextGridPoint) {
      let entryX = 0, entryY = 0, exitX = 0, exitY = 0;

      if (fromDir === 'W') entryX = -offset;
      else if (fromDir === 'E') entryX = offset;
      else if (fromDir === 'N') entryY = -offset;
      else if (fromDir === 'S') entryY = offset;

      if (toDir === 'E') exitX = offset;
      else if (toDir === 'W') exitX = -offset;
      else if (toDir === 'S') exitY = offset;
      else if (toDir === 'N') exitY = -offset;

      if (fromDir === toDir || (fromDir === 'N' && toDir === 'S') || (fromDir === 'S' && toDir === 'N') ||
         (fromDir === 'E' && toDir === 'W') || (fromDir === 'W' && toDir === 'E')) {
        offsetX = (entryX + exitX) / 2;
        offsetY = (entryY + exitY) / 2;
      } else {
        offsetX = (entryX + exitX) * 0.6;
        offsetY = (entryY + exitY) * 0.6;
      }
    }

    return {x: offsetX, y: offsetY};
  }

  function drawPathAvoidingDots(ctx, x1Px, y1Px, x2Px, y2Px, fromGrid, toGrid, gridSize, occupiedCells, cellSize, pad, allDotPositions) {
    const gridPath = findPathAvoidingDots(fromGrid, toGrid, gridSize, occupiedCells);

    const pixelPath = [];
    for (let i = 0; i < gridPath.length; i++) {
      const p = gridPath[i];
      const prev = i > 0 ? gridPath[i - 1] : null;
      const next = i < gridPath.length - 1 ? gridPath[i + 1] : null;

      const baseX = pad + p.x * cellSize + cellSize / 2;
      const baseY = pad + p.y * cellSize + cellSize / 2;

      const isDot = allDotPositions.has(key(p));

      const offset = calculateSmartAnchor(p, prev, next, cellSize, isDot);

      pixelPath.push({
        x: baseX + offset.x,
        y: baseY + offset.y
      });
    }

    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.arc(pixelPath[0].x, pixelPath[0].y, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(pixelPath[0].x, pixelPath[0].y);
    for (let i = 1; i < pixelPath.length; i++) {
      ctx.lineTo(pixelPath[i].x, pixelPath[i].y);
    }
    ctx.stroke();

    const arrowSize = 12;
    const lastIdx = pixelPath.length - 1;
    if (lastIdx >= 1) {
      const secondLast = pixelPath[lastIdx - 1];
      const last = pixelPath[lastIdx];
      const dx = last.x - secondLast.x;
      const dy = last.y - secondLast.y;
      const angle = Math.atan2(dy, dx);

      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(
        last.x - arrowSize * Math.cos(angle - Math.PI / 6),
        last.y - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(
        last.x - arrowSize * Math.cos(angle + Math.PI / 6),
        last.y - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.stroke();
    }
  }

  function drawBoard(ctx, grid, drawOrderLines = false, orderSeq = null) {
    drawGridOnly(ctx, grid);
    const gridH = CONFIG.CELL * grid.size + CONFIG.PAD * 2;
    let y = gridH + 22;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.font = 'bold 18px Arial';
    ctx.fillStyle = '#333';
    ctx.fillText('Find the order of dots visited:', 20, y);
    y += 26;
    ctx.font = '16px Arial';
    ctx.fillStyle = '#333';
    ctx.fillText('Start: ' + grid.stepCounts.join(', '), 20, y);

    if (drawOrderLines && orderSeq && orderSeq.length) {
      y += 30;
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#007700';
      const solutionNumbers = orderSeq.map(i => circleGlyph[i + 1] || String(i + 1)).join(', ');
      ctx.fillText('Solution: ' + solutionNumbers, 20, y);
    }

    if (drawOrderLines && orderSeq && orderSeq.length) {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2.0;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const cs = CONFIG.CELL, pad = CONFIG.PAD;
      const pts = [grid.start, ...orderSeq.map(i => grid.dots[i])];

      const occupied = new Set();
      occupied.add(key(grid.start));
      for (const dot of grid.dots) {
        occupied.add(key(dot));
      }

      const allDotPositions = new Set(occupied);

      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const x1 = pad + a.x * cs + cs / 2, y1 = pad + a.y * cs + cs / 2;
        const x2 = pad + b.x * cs + cs / 2, y2 = pad + b.y * cs + cs / 2;

        const tempOccupied = new Set(occupied);
        tempOccupied.delete(key(a));
        tempOccupied.delete(key(b));

        drawPathAvoidingDots(ctx, x1, y1, x2, y2, a, b, grid.size, tempOccupied, cs, pad, allDotPositions);
      }
    }
  }

  function drawSolutionOnly(ctx, grid, orderSeq) {
    drawGridOnly(ctx, grid);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2.0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const cs = CONFIG.CELL, pad = CONFIG.PAD;
    const pts = [grid.start, ...orderSeq.map(i => grid.dots[i])];

    const occupied = new Set();
    occupied.add(key(grid.start));
    for (const dot of grid.dots) {
      occupied.add(key(dot));
    }

    const allDotPositions = new Set(occupied);

    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const x1 = pad + a.x * cs + cs / 2, y1 = pad + a.y * cs + cs / 2;
      const x2 = pad + b.x * cs + cs / 2, y2 = pad + b.y * cs + cs / 2;

      const tempOccupied = new Set(occupied);
      tempOccupied.delete(key(a));
      tempOccupied.delete(key(b));

      drawPathAvoidingDots(ctx, x1, y1, x2, y2, a, b, grid.size, tempOccupied, cs, pad, allDotPositions);
    }
  }

  function triggerDownloadFromCanvas(canvas, filename) {
    try {
      if (canvas.toBlob) {
        canvas.toBlob((blob) => {
          if (!blob) {
            const dataURL = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataURL;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 0);
        }, 'image/png');
      } else {
        const dataURL = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      console.error('Export fallback: opening image in new tab', e);
      const dataURL = canvas.toDataURL('image/png');
      window.open(dataURL, '_blank');
    }
  }

  const handleGenerate = async () => {
    const n = Math.max(4, Math.min(10, gridSize));
    const K = Math.max(3, Math.min(7, stepsCount));

    setGenerating(true);
    setProgress({ show: true, percent: 0, tested: 0, score: 0 });

    const progressCallback = (cur, total, tested, score) => {
      const pct = Math.round((cur / total) * 100);
      setProgress({ show: true, percent: pct, tested, score });
    };

    await new Promise(r => setTimeout(r, 20));

    const best = generateBestPuzzle(n, K, iterations, progressCallback);

    setProgress({ show: false, percent: 0, tested: 0, score: 0 });
    setGenerating(false);

    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const cssW = CONFIG.WIDTH;
    const cssH = CONFIG.CELL * (best ? best.grid.size : 6) + CONFIG.PAD * 2 + 120;
    const ctx = setupCanvas(canvas, cssW, cssH);

    if (!best) {
      ctx.font = '16px Arial';
      ctx.fillStyle = '#c00';
      ctx.fillText('No unique puzzle found. Try more iterations or smaller K.', 30, 60);
      return;
    }

    setPuzzle(best);
    setShowingSolution(false);

    drawBoard(ctx, best.grid, false, null);

    const a = best.analysis;
    const maxDist = Math.max(...best.grid.stepCounts);
    const lastDist = best.grid.stepCounts[best.grid.stepCounts.length - 1];
    const collapsePos = a.collapseStep <= K / 3 ? 'early' : (a.collapseStep >= 2 * K / 3 ? 'late' : 'middle');

    setInfo({
      score: best.score,
      maxWidth: a.maxWidth,
      ambiguousSteps: a.ambiguousSteps,
      collapseStep: a.collapseStep,
      totalSteps: best.grid.stepCounts.length,
      collapsePos,
      minDist: Math.min(...best.grid.stepCounts),
      maxDist,
      detours: a.detours,
      lastIsMax: lastDist === maxDist
    });
  };

  const handleToggleSolution = () => {
    if (!puzzle || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const cssW = CONFIG.WIDTH;
    const cssH = CONFIG.CELL * puzzle.grid.size + CONFIG.PAD * 2 + 120;
    const ctx = setupCanvas(canvas, cssW, cssH);

    const newShowingSolution = !showingSolution;
    setShowingSolution(newShowingSolution);

    if (newShowingSolution) {
      drawBoard(ctx, puzzle.grid, true, puzzle.analysis.orders[0]);
    } else {
      drawBoard(ctx, puzzle.grid, false, null);
    }
  };

  const handleExportTask = () => {
    if (!puzzle) {
      alert('Please generate a puzzle first!');
      return;
    }
    const cssW = CONFIG.CELL * puzzle.grid.size + CONFIG.PAD * 2;
    const cssH = CONFIG.CELL * puzzle.grid.size + CONFIG.PAD * 2;
    const canvas = document.createElement('canvas');
    const ctx = setupCanvas(canvas, cssW, cssH);
    drawGridOnly(ctx, puzzle.grid);
    triggerDownloadFromCanvas(canvas, 'detective-dots-task.png');
  };

  const handleExportSolution = () => {
    if (!puzzle) {
      alert('Please generate a puzzle first!');
      return;
    }
    const cssW = CONFIG.CELL * puzzle.grid.size + CONFIG.PAD * 2;
    const cssH = CONFIG.CELL * puzzle.grid.size + CONFIG.PAD * 2;
    const canvas = document.createElement('canvas');
    const ctx = setupCanvas(canvas, cssW, cssH);
    drawSolutionOnly(ctx, puzzle.grid, puzzle.analysis.orders[0]);
    triggerDownloadFromCanvas(canvas, 'detective-dots-solution.png');
  };

  return (
    <div className="dot-split-generator">
      <h1>🌻 Garden Walk — Order-from-Steps Generator</h1>
      <p className="subtitle">Arrows on solution, crisp text, and robust PNG export.</p>

      <div className="controls">
        <div className="control-group">
          <div className="control-item">
            <label>Grid Size (4–10)</label>
            <input
              type="number"
              value={gridSize}
              onChange={(e) => setGridSize(parseInt(e.target.value))}
              min="4"
              max="10"
              step="1"
            />
            <div className="note">any integer from 4 to 10</div>
          </div>
          <div className="control-item">
            <label># of Dots Visited (K)</label>
            <input
              type="number"
              value={stepsCount}
              onChange={(e) => setStepsCount(parseInt(e.target.value))}
              min="3"
              max="7"
            />
            <div className="note">length of the clue</div>
          </div>
          <div className="control-item">
            <label>Search Iterations</label>
            <input
              type="number"
              value={iterations}
              onChange={(e) => setIterations(parseInt(e.target.value))}
              min="100"
              max="30000"
            />
            <div className="note">more = slower but better</div>
          </div>
        </div>
        <div className="buttons">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn-primary"
          >
            {generating ? '⏳ Generating...' : '🎲 Generate Best Puzzle'}
          </button>
          {puzzle && (
            <>
              <button onClick={handleToggleSolution} className="btn-outline">
                {showingSolution ? '🔒 Hide Solution' : '👁️ Show Solution'}
              </button>
              <button onClick={handleExportTask} className="btn-secondary">
                📥 Export Task (grid only)
              </button>
              <button onClick={handleExportSolution} className="btn-secondary">
                📥 Export Solution (grid only)
              </button>
            </>
          )}
        </div>
        {progress.show && (
          <div className="progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress.percent}%` }}>
                {progress.percent}% (tested {progress.tested}, best {progress.score})
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="canvas-wrap">
        <canvas ref={canvasRef} width="600" height="720"></canvas>
      </div>

      {info && (
        <div className="info">
          <div className="kpi">
            <div><strong>Branching score</strong><br />{info.score}</div>
            <div><strong>Peak width</strong><br />{info.maxWidth}</div>
            <div><strong>Ambiguous steps</strong><br />{info.ambiguousSteps}</div>
            <div><strong>Collapse point</strong><br />{info.collapseStep}/{info.totalSteps} ({info.collapsePos})</div>
            <div><strong>Distance range</strong><br />{info.minDist}-{info.maxDist}</div>
            <div><strong>Detours</strong><br />{info.detours > 0 ? '🎯 ' + info.detours : '0'}</div>
            <div><strong>Max at end?</strong><br />{info.lastIsMax ? '⚠️ Yes' : '✓ No'}</div>
            <div><strong>Unique solution</strong><br />✔</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DotSplitGenerator;
